import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, forkJoin, Observable, of, switchMap } from 'rxjs';
import { environment } from '../environments/environment';
import { KeycloakGnService } from './shared/services/keycloak-gn.service';
import { UserInfo, UserProfile } from './shared/utils/utils.auth.types';

export function httpInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const keycloakService = inject(KeycloakGnService);
  const routeUrl = inject(Router).url ?? '';

  if (req.url.includes('/broker/oidc/token')) {
    return next(
      req.clone({
        setHeaders: {
          Authorization: `Bearer ${keycloakService.instance.token}`,
        },
      }),
    );
  }

  if (
    !routeUrl.trim() ||
    req.url.includes('i18n') ||
    req.url.includes('/live-interview-p') ||
    req.url.includes('/auto-interview-p') ||
    (routeUrl.includes('/meetcall') && !req.url.includes('/lya/convuman/events/save-comment')) ||
    routeUrl.includes('/lya-space/') ||
    routeUrl.includes('/demo/1g-cv') ||
    req.url.includes('lya/p-candidates')
  ) {
    const url =
      req.url.startsWith('http') || req.url.includes('i18n')
        ? req.url
        : `${environment.apiUrl}${req.url}`;
    return next(req.clone({ url }));
  }

  // Do not wait on the NGXS router selector. It only emits when navigation
  // ends, and this interceptor runs from a resolver during that navigation,
  // so the shell never painted. Profile and company are best-effort: a hung
  // Keycloak call must not block the gateway request.
  return forkJoin({
    profile: keycloakService
      .loadUserProfile()
      .pipe(catchError(() => of(null as UserProfile | null))),
    info: keycloakService.loadUserInfo().pipe(catchError(() => of(null as UserInfo | null))),
  }).pipe(
    switchMap(({ profile, info }) => {
      const headers: Record<string, string> = {};
      const token = keycloakService.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      if (profile?.id) {
        headers['user_id'] = profile.id;
      }
      if (info?.company) {
        headers['company_id'] = info.company;
      }
      return next(
        req.clone({
          setHeaders: headers,
          url: req.url.startsWith('http') ? req.url : `${environment.apiUrl}${req.url}`,
        }),
      );
    }),
  );
}
